import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, Input, NgZone, OnChanges, OnDestroy, PLATFORM_ID } from '@angular/core';
import { BaseChartComponent, calculateViewDimensions, ViewDimensions } from '@swimlane/ngx-charts';
import { ScaleBand, scaleBand } from 'd3-scale';
import { coloreClasse, HeatmapCella } from '../../../../../models/rischio-analitico.model';

interface CellaRect {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  cella: HeatmapCella;
}

/**
 * Heatmap punto x parametro (asse X = parametro, asse Y = punto), con la cella colorata in base
 * alla classe di rischio. Componente custom (non ngx-charts-heat-map, pensato per gradienti su
 * valori continui) che riusa l'infrastruttura di BaseChartComponent, sullo stesso schema di
 * RischioAnaliticoTrendChartComponent.
 */
@Component({
  selector: 'ebw-monorepo-rischio-analitico-heatmap-chart',
  templateUrl: './rischio-analitico-heatmap-chart.component.html',
  styleUrls: ['./rischio-analitico-heatmap-chart.component.scss']
})
export class RischioAnaliticoHeatmapChartComponent extends BaseChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() celle: HeatmapCella[] = [];
  // ordine delle righe (punti) della tabella del Riepilogo: i punti che non vi compaiono restano in fondo
  @Input() ordinePunti: string[] = [];

  margin = [10, 20, 40, 10];
  xAxisHeight = 0;
  yAxisWidth = 0;

  dims: ViewDimensions;
  xScale: ScaleBand<string>;
  yScale: ScaleBand<string>;
  transform: string;
  rects: CellaRect[] = [];

  // getContainerDims() di ngx-charts misura la larghezza al momento della creazione del componente:
  // dentro uno split-view che si attacca al DOM con un piccolo ritardo puo' misurare un valore
  // sbagliato (o 0) e restare agganciato a quello per sempre. Qui teniamo la larghezza sincronizzata
  // con quella reale dell'host, a prescindere da quando viene misurata la prima volta.
  private _hostWidth: number | null = null;
  private _resizeObserver: ResizeObserver | undefined;

  constructor(chartElement: ElementRef, zone: NgZone, cd: ChangeDetectorRef, @Inject(PLATFORM_ID) private _platformId: object) {
    super(chartElement, zone, cd, _platformId);
    this.animations = false;
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    if (!isPlatformBrowser(this._platformId)) return;

    this._resizeObserver = new ResizeObserver((entries) => {
      const larghezza = Math.floor(entries[0]?.contentRect?.width ?? 0);
      if (larghezza > 0 && larghezza !== this._hostWidth) {
        this._hostWidth = larghezza;
        this.update();
      }
    });
    this._resizeObserver.observe(this.chartElement.nativeElement);
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    super.ngOnDestroy();
  }

  update(): void {
    super.update();
    if (this._hostWidth) {
      this.width = this._hostWidth;
    }
    if (!this.celle?.length) {
      this.rects = [];
      return;
    }

    const posizione = (punto: string) => {
      const indice = this.ordinePunti.indexOf(punto);
      return indice === -1 ? Number.MAX_SAFE_INTEGER : indice;
    };
    const punti = [...new Set(this.celle.map((c) => c.punto))].sort((a, b) => posizione(a) - posizione(b));
    const parametri = [...new Set(this.celle.map((c) => c.parameter))];

    // l'altezza segue il contenuto (una fascia per punto), non il contenitore: con molti
    // punti sull'asse Y la heatmap deve crescere in verticale (e la sezione scrollare),
    // non restare schiacciata dentro un riquadro di dimensione fissa
    const altezzaRiga = 40;
    this.height = Math.max(200, punti.length * altezzaRiga + this.margin[0] + this.margin[2] + this.xAxisHeight);

    this.dims = calculateViewDimensions({
      width: this.width,
      height: this.height,
      margins: this.margin,
      showXAxis: true,
      showYAxis: true,
      xAxisHeight: this.xAxisHeight,
      yAxisWidth: this.yAxisWidth
    });

    this.xScale = scaleBand<string>().range([0, this.dims.width]).padding(0.05).domain(parametri);
    this.yScale = scaleBand<string>().range([0, this.dims.height]).padding(0.05).domain(punti);

    this.rects = this.celle.map((cella) => ({
      x: this.xScale(cella.parameter) ?? 0,
      y: this.yScale(cella.punto) ?? 0,
      width: this.xScale.bandwidth(),
      height: this.yScale.bandwidth(),
      fill: coloreClasse(cella.risk_class, 'last'),
      cella
    }));

    this.transform = `translate(${this.dims.xOffset}, ${this.margin[0]})`;
  }

  updateXAxisHeight(event: { height: number }): void {
    this.xAxisHeight = event.height;
    this.update();
  }

  updateYAxisWidth(event: { width: number }): void {
    this.yAxisWidth = event.width;
    this.update();
  }

  titoloCella(cella: HeatmapCella): string {
    return `${cella.punto} — ${cella.parameter} (${cella.um}): ${cella.risk_class}, ${cella.n_obs} osservazioni`;
  }

  formattaParametro = (valore: string): string => valore.replace(/_/g, ' ');
}
